import { useEffect, useMemo, useState } from 'react';
import { Navigate, Link, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../../api/errors';
import { Route42InlineMarkdown, Route42Markdown } from '../../../components/ui/Route42Markdown';
import { queryKeys } from '../../../api/query-keys';
import { useAuth } from '../../../auth/AuthProvider';
import { permissions } from '../../../auth/permissions';
import { askQuestion } from '../../conversations/api/conversations.api';
import { useConversation } from '../../conversations/api/useConversations';
import { useChatAutoScroll } from '../../conversations/hooks/useChatAutoScroll';
import { useThreadHistoryLoader } from '../../conversations/hooks/useThreadHistoryLoader';
import { EvidencePanel } from '../../conversations/components/EvidencePanel';
import { ThinkingState } from '../../conversations/components/ThinkingState';
import type { GroundedAnswerDto } from '../../conversations/types/conversation';
import { useDocuments } from '../../documents/api/useDocuments';
import { useMachines } from '../../machines/api/useMachines';
import { scheduleInvestigationV1AcceptedReview, getInvestigationV1EvidenceReviews, submitInvestigationV1EvidenceReview, submitInvestigationV1Feedback, addInvestigationContent, postInvestigationUpdate, refreshInvestigationReasoning, respondToInvestigationInquiry, reviewInvestigationMachineKnowledge, updateInvestigationStatus } from '../api/investigations.api';
import {
  useInvestigation,
  useInvestigationInquiries,
  useInvestigationReasoning,
  useInvestigationReasoningV1,
  useInvestigationReasoningFreshness,
  useInvestigationReasoningHistory,
} from '../api/useInvestigations';
import { ActiveReasoningPanel } from '../components/ActiveReasoningPanel';
import { ActiveReasoningV1Panel } from '../components/ActiveReasoningV1Panel';
import { InvestigationOperationalOverview } from '../components/InvestigationOperationalOverview';
import type { DiagnosticV1EvidenceReviewDraft, DiagnosticV1FeedbackDraft } from '../types/active-reasoning-v1';
import { InvestigationExpandableText } from '../components/InvestigationExpandableText';
import { AddInvestigationContentDialog } from '../components/AddInvestigationContentDialog';
import { CloseInvestigationDialog } from '../components/CloseInvestigationDialog';
import { InvestigationClosureSummary } from '../components/InvestigationClosureSummary';
import { InvestigationAiRoleStrip } from '../components/InvestigationAiRole';
import { InvestigationComposer } from '../components/InvestigationComposer';
import { InvestigationThreadEntry } from '../components/InvestigationThreadEntry';
import { InvestigationKnowledgeSection } from '../components/InvestigationKnowledgeSection';
import { InvestigationSynthesisCard } from '../components/InvestigationSynthesisCard';
import { useInvestigationConversationFocus } from '../hooks/useInvestigationConversationFocus';
import { PinnedEvidencePanel } from '../components/PinnedEvidencePanel';
import type { InquiryResponseActionDto, ActiveReasoningInquiryDto } from '../types/active-reasoning';
import type { InvestigationClosureInputDto, InvestigationContentTypeDto, InvestigationMemoryItemDto, PinnedEvidenceDto } from '../types/investigation';
import { investigationStatusLabel } from '../types/investigation';

export function InvestigationDetailPage() {
  const { machineId = '', investigationId = '' } = useParams();
  const auth = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const investigation = useInvestigation(investigationId, auth.can(permissions.investigationRead));
  const activeReasoningV1 = useInvestigationReasoningV1(investigationId, auth.can(permissions.investigationRead));
  const selectedV1 = activeReasoningV1.data?.writer === 'V1';
  const selectedLegacy = activeReasoningV1.data?.writer === 'LEGACY';
  const activeReasoning = useInvestigationReasoning(investigationId, auth.can(permissions.investigationRead) && selectedLegacy);
  const reasoningFreshness = useInvestigationReasoningFreshness(investigationId, auth.can(permissions.investigationRead) && selectedLegacy);
  const reasoningInquiries = useInvestigationInquiries(investigationId, auth.can(permissions.investigationRead) && selectedLegacy);
  const reasoningHistory = useInvestigationReasoningHistory(investigationId, auth.can(permissions.investigationRead) && selectedLegacy);
  const machines = useMachines();
  const documents = useDocuments(machineId, auth.can(permissions.documentsRead));
  const machine = machines.data?.machines.find((item) => item.id === machineId);
  const conversationId = investigation.data?.investigation.conversation_ids?.[0] ?? '';
  const conversation = useConversation(conversationId, Boolean(conversationId && auth.can(permissions.conversationRead)));
  const [workspaceView, setWorkspaceView] = useState<'operational' | 'advanced'>('operational');
  const [composerFocus, setComposerFocus] = useState<{ destination: 'team' | 'ace'; requestId: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [threadAction, setThreadAction] = useState<'post' | 'ask' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [evidence, setEvidence] = useState<GroundedAnswerDto | null>(null);
  const [pinnedEvidence, setPinnedEvidence] = useState<PinnedEvidenceDto | null>(null);
  const [contentType, setContentType] = useState<InvestigationContentTypeDto | null>(null);
  const [contentBusy, setContentBusy] = useState(false);
  const [contentError, setContentError] = useState<string | null>(null);
  const [reasoningActionBusy, setReasoningActionBusy] = useState<string | null>(null);
  const [reasoningRetryBusy, setReasoningRetryBusy] = useState(false);
  const [reasoningActionError, setReasoningActionError] = useState<string | null>(null);
  const [showClose, setShowClose] = useState(false);
  const [visiblePinnedEvidenceCount, setVisiblePinnedEvidenceCount] = useState(10);
  const conversationFocus = useInvestigationConversationFocus({
    investigationId,
    evidenceOpen: Boolean(evidence),
    pinnedEvidenceOpen: Boolean(pinnedEvidence),
    dialogOpen: Boolean(contentType) || showClose,
    onCloseEvidence: () => setEvidence(null),
    onClosePinnedEvidence: () => setPinnedEvidence(null),
  });
  const allInvestigationTurns = conversation.data?.turns ?? [];
  const investigationChatScroll = useChatAutoScroll({
    threadKey: conversationId || investigationId,
    itemCount: allInvestigationTurns.length,
    busy: threadAction !== null,
  });
  const historyLoader = useThreadHistoryLoader({
    viewportRef: investigationChatScroll.viewportRef,
    itemCount: allInvestigationTurns.length,
    fetchOlder: conversation.fetchOlder,
  });
  const hiddenTurnCount = Math.max(0, (conversation.data?.turn_count ?? allInvestigationTurns.length) - allInvestigationTurns.length);

  useEffect(() => {
    setVisiblePinnedEvidenceCount(10);
    setWorkspaceView('operational');
    setComposerFocus(null);
  }, [investigationId]);

  useEffect(() => {
    const freshness = reasoningFreshness.data?.freshness;
    const displayedVersion = activeReasoning.data?.reasoning_state?.reasoning_version ?? null;
    if (freshness?.reasoning_status !== 'CURRENT' || !freshness.current_reasoning_version || freshness.current_reasoning_version === displayedVersion) return;
    void Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.investigationReasoning(auth.activeCustomerId, investigationId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.investigationInquiries(auth.activeCustomerId, investigationId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.investigationReasoningHistory(auth.activeCustomerId, investigationId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.investigation(auth.activeCustomerId, investigationId) }),
    ]);
  }, [
    activeReasoning.data?.reasoning_state?.reasoning_version,
    auth.activeCustomerId,
    investigationId,
    queryClient,
    reasoningFreshness.data?.freshness,
  ]);

  const data = investigation.data;
  const memory = data?.investigation_memory;
  const assessment = useMemo(() => latest(memory?.conclusions) ?? null, [memory]);
  const findings = memory?.findings ?? [];
  const hypotheses = memory?.working_hypotheses ?? [];
  const questions = memory?.open_questions ?? [];
  const participantNames = useMemo(
    () => new Map((data?.participant_profiles ?? []).map((profile) => [profile.user_id, profile.display_name])),
    [data?.participant_profiles],
  );
  const visiblePinnedEvidence = data?.pinned_evidence.slice(0, visiblePinnedEvidenceCount) ?? [];
  const hiddenPinnedEvidenceCount = Math.max(0, (data?.pinned_evidence.length ?? 0) - visiblePinnedEvidence.length);
  const reasoningState = activeReasoning.data?.reasoning_state ?? null;
  const reasoningInquiryItems = reasoningInquiries.data?.reasoning_version === reasoningState?.reasoning_version
    ? reasoningInquiries.data?.inquiries ?? []
    : reasoningState?.candidate_inquiries ?? [];

  async function submitV1EvidenceReview(draft: DiagnosticV1EvidenceReviewDraft) {
    await submitInvestigationV1EvidenceReview(investigationId, draft);
    await queryClient.invalidateQueries({
      queryKey: queryKeys.investigationReasoningV1(auth.activeCustomerId, investigationId),
    });
  }

  if (!auth.can(permissions.investigationRead)) return <Navigate to={`/machines/${machineId}`} replace />;
  if (investigation.isPending) return <section className="r42-investigation-page"><div className="r42-inline-state" role="status">Loading investigation…</div></section>;
  if (investigation.isError || !data) return <section className="r42-investigation-page"><div className="r42-inline-state r42-inline-state--error"><span>Route42 couldn't load this investigation.</span><button className="btn btn-sm btn-outline-secondary" onClick={() => void investigation.refetch()}>Try again</button></div></section>;
  if (data.investigation.machine_id !== machineId) return <Navigate to={`/machines/${machineId}/investigations`} replace />;

  const status = data.investigation.status;
  const canUpdate = auth.can(permissions.investigationUpdate) && (data.access?.can_manage ?? data.investigation.owner_user_id === auth.profile?.user.user_id);
  const canContribute = auth.can(permissions.investigationContribute) && (data.access?.can_contribute ?? true) && status !== 'CLOSED' && status !== 'ARCHIVED';
  const canAsk = canContribute && auth.can(permissions.questionAsk) && auth.can(permissions.conversationRead) && Boolean(conversationId);
  function focusOperationalComposer(destination: 'team' | 'ace') {
    setComposerFocus((previous) => ({ destination, requestId: (previous?.requestId ?? 0) + 1 }));
  }
  const pageClassName = [
    'r42-investigation-page',
    workspaceView === 'operational' ? 'r42-investigation-page--operational' : 'r42-investigation-page--advanced',
    conversationFocus.isFocused ? 'r42-investigation-page--conversation-focus' : '',
    conversationFocus.isFocused && (evidence || pinnedEvidence) ? 'r42-investigation-page--evidence-open' : '',
  ].filter(Boolean).join(' ');

  async function changeStatus(next: 'OPEN') {
    setBusy(true); setError(null);
    try { await updateInvestigationStatus(investigationId, next); await queryClient.invalidateQueries({ queryKey: queryKeys.investigation(auth.activeCustomerId, investigationId) }); await queryClient.invalidateQueries({ queryKey: queryKeys.investigations(auth.activeCustomerId, machineId) }); }
    catch (value) { setError(apiMessage(value)); } finally { setBusy(false); }
  }

  async function closeInvestigation(closure: InvestigationClosureInputDto) {
    setBusy(true); setError(null);
    try {
      await updateInvestigationStatus(investigationId, 'CLOSED', closure);
      await queryClient.invalidateQueries({ queryKey: queryKeys.investigation(auth.activeCustomerId, investigationId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.investigations(auth.activeCustomerId, machineId) });
      setShowClose(false);
    } catch (value) { setError(apiMessage(value)); } finally { setBusy(false); }
  }

  async function invalidateActiveReasoning() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.investigationReasoningV1(auth.activeCustomerId, investigationId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.investigationReasoning(auth.activeCustomerId, investigationId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.investigationReasoningFreshness(auth.activeCustomerId, investigationId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.investigationInquiries(auth.activeCustomerId, investigationId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.investigationReasoningHistory(auth.activeCustomerId, investigationId) }),
    ]);
  }

  async function saveContent(text: string) {
    if (!contentType) return;
    setContentBusy(true); setContentError(null);
    try {
      await addInvestigationContent(investigationId, contentType, text);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.investigation(auth.activeCustomerId, investigationId) }),
        invalidateActiveReasoning(),
      ]);
      setContentType(null);
    }
    catch (value) { setContentError(apiMessage(value)); } finally { setContentBusy(false); }
  }

  async function postTeamUpdate(text: string) {
    investigationChatScroll.scrollToLatest();
    setThreadAction('post'); setError(null);
    try {
      await postInvestigationUpdate(investigationId, text);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.conversation(auth.activeCustomerId, conversationId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.investigation(auth.activeCustomerId, investigationId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.investigations(auth.activeCustomerId, machineId) }),
        invalidateActiveReasoning(),
      ]);
    } catch (value) { setError(apiMessage(value)); throw value; } finally { setThreadAction(null); }
  }

  async function askAce(question: string) {
    if (!conversationId) return;
    investigationChatScroll.scrollToLatest();
    setThreadAction('ask'); setError(null);
    try {
      await askQuestion(conversationId, question);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.conversation(auth.activeCustomerId, conversationId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.investigation(auth.activeCustomerId, investigationId) }),
        invalidateActiveReasoning(),
      ]);
    }
    catch (value) { setError(apiMessage(value)); throw value; } finally { setThreadAction(null); }
  }

  async function respondToReasoningInquiry(inquiry: ActiveReasoningInquiryDto, responseType: InquiryResponseActionDto, text?: string) {
    setReasoningActionBusy(inquiry.inquiry_id);
    setReasoningActionError(null);
    try {
      await respondToInvestigationInquiry(investigationId, inquiry.inquiry_id, responseType, text);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.investigation(auth.activeCustomerId, investigationId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.investigationReasoning(auth.activeCustomerId, investigationId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.investigationReasoningFreshness(auth.activeCustomerId, investigationId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.investigationInquiries(auth.activeCustomerId, investigationId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.investigationReasoningHistory(auth.activeCustomerId, investigationId) }),
      ]);
    } catch (value) {
      setReasoningActionError(apiMessage(value));
    } finally {
      setReasoningActionBusy(null);
    }
  }

  async function retryActiveReasoning() {
    setReasoningRetryBusy(true);
    setReasoningActionError(null);
    try {
      await refreshInvestigationReasoning(investigationId);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.investigation(auth.activeCustomerId, investigationId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.investigationReasoning(auth.activeCustomerId, investigationId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.investigationReasoningFreshness(auth.activeCustomerId, investigationId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.investigationInquiries(auth.activeCustomerId, investigationId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.investigationReasoningHistory(auth.activeCustomerId, investigationId) }),
      ]);
    } catch (value) {
      setReasoningActionError(apiMessage(value));
    } finally {
      setReasoningRetryBusy(false);
    }
  }

  async function submitV1Feedback(draft: DiagnosticV1FeedbackDraft): Promise<void> {
    const receipt = await submitInvestigationV1Feedback(investigationId, draft);
    // The C.18 outbox is durable; PENDING_NOT_DISPATCHED is NOT a new assessment.
    if (!receipt.feedback_id || receipt.effect !== 'UNVERIFIED_HUMAN_FEEDBACK_ONLY') {
      throw new Error('Feedback receipt was not confirmed');
    }
    await queryClient.invalidateQueries({
      queryKey: queryKeys.investigationReasoningV1(auth.activeCustomerId, investigationId),
    });
  }

  function addEvidenceForInquiry(inquiry: ActiveReasoningInquiryDto) {
    const returnTo = `/machines/${machineId}/investigations/${investigationId}`;
    const params = new URLSearchParams({ investigationId, inquiryId: inquiry.inquiry_id, returnTo });
    navigate(`/machines/${machineId}/documents/add?${params.toString()}`);
  }

  function openReasoningEvidence(reference: string) {
    const snapshot = activeReasoning.data?.reasoning_state?.evidence_snapshot;
    const snapshotReference = snapshot ? [
      ...snapshot.investigation_evidence,
      ...snapshot.documents,
      ...snapshot.messages,
      ...snapshot.findings,
      ...snapshot.human_answers,
      ...snapshot.observations,
    ].find((item) => item.evidence_reference === reference) : undefined;
    const sourceCandidates = new Set([
      reference,
      snapshotReference?.source_id,
      snapshotReference?.document_id,
    ].filter((value): value is string => Boolean(value)));
    const match = data?.pinned_evidence.find((item) => sourceCandidates.has(item.evidence_id) || sourceCandidates.has(item.artifact_id));
    if (match) {
      setEvidence(null);
      setPinnedEvidence(match);
    }
  }

  return <section className={pageClassName}>
    <header className="r42-investigation-head">
      <div className="r42-investigation-head-copy"><Link className="r42-back-link" to={`/machines/${machineId}/investigations`}><i className="bi bi-arrow-left" /> Investigations</Link><span className="r42-eyebrow">{machine?.name ?? 'Machine'} investigation</span><h1>{data.investigation.title}</h1><InvestigationExpandableText text={data.investigation.objective} maxCharacters={360} className="r42-investigation-head-objective" /></div>
      <div className="r42-investigation-head-actions"><span className={`r42-investigation-status r42-investigation-status--${status.toLowerCase()}`}>{investigationStatusLabel(status)}</span>{canUpdate && status !== 'CLOSED' && status !== 'ARCHIVED' ? <button className="btn btn-outline-secondary" onClick={() => { setError(null); setShowClose(true); }} disabled={busy}>Close investigation</button> : null}{canUpdate && status === 'CLOSED' ? <button className="btn btn-outline-secondary" onClick={() => void changeStatus('OPEN')} disabled={busy}>Reopen investigation</button> : null}</div>
    </header>
    <nav className="r42-investigation-view-switch" aria-label="Investigation view">
      <span className="r42-investigation-view-switch__label">Workspace view</span>
      <div className="r42-investigation-view-switch__options">
        <button type="button" className={workspaceView === 'operational' ? 'is-active' : ''} aria-pressed={workspaceView === 'operational'} onClick={() => setWorkspaceView('operational')}>Operational</button>
        <button type="button" className={workspaceView === 'advanced' ? 'is-active' : ''} aria-pressed={workspaceView === 'advanced'} onClick={() => setWorkspaceView('advanced')}>Advanced</button>
      </div>
    </nav>
    {error ? <div className="r42-question-error" role="alert"><i className="bi bi-exclamation-circle" /><span>{error}</span></div> : null}

    <div className="r42-investigation-layout">
      <div className="r42-investigation-main">
        {status === 'CLOSED' && data.investigation.closure ? <InvestigationClosureSummary closure={data.investigation.closure} machineId={machineId} machineKnowledge={data.machine_knowledge ?? []} /> : null}

        {workspaceView === 'operational' && !conversationFocus.isFocused ? <InvestigationOperationalOverview
          view={selectedV1 ? activeReasoningV1.data?.view ?? null : null}
          loading={activeReasoningV1.isPending}
          onOpenAdvanced={() => setWorkspaceView('advanced')}
          canDiscuss={canContribute && auth.can(permissions.conversationRead) && Boolean(conversationId)}
          canAskAce={canAsk}
          onDiscussWithTeam={() => focusOperationalComposer('team')}
          onAskAce={() => focusOperationalComposer('ace')}
          canReviewClosure={canUpdate && status === 'OPEN'}
          onReviewClosure={() => { setError(null); setShowClose(true); }}
        /> : null}

        {workspaceView === 'advanced' && selectedLegacy ? <section className="r42-investigation-section r42-investigation-assessment"><div className="r42-investigation-section-head"><div><span className="r42-eyebrow">Where things stand</span><h2>What the team currently understands</h2></div></div>{assessment ? <Route42Markdown className="r42-investigation-assessment-text" content={assessment.text} /> : <div className="r42-investigation-section-empty">Route42 hasn't formed a shared assessment yet. Keep working in the shared Investigation or add evidence; this section updates when new information materially changes the team's understanding.</div>}</section> : null}

        {workspaceView === 'advanced' && selectedLegacy && data.latest_synthesis ? <InvestigationSynthesisCard synthesis={data.latest_synthesis} /> : null}

        {workspaceView === 'advanced' && !conversationFocus.isFocused && (selectedV1 && activeReasoningV1.data?.view) ? <ActiveReasoningV1Panel
          view={activeReasoningV1.data.view}
          canContribute={canContribute}
          canReviewEvidence={canContribute}
          canReview={canUpdate && status !== 'CLOSED' && status !== 'ARCHIVED'}
          onSubmitFeedback={submitV1Feedback}
          onSubmitEvidenceReview={submitV1EvidenceReview}
          onScheduleAcceptedReview={async (reviewId) => {
            await scheduleInvestigationV1AcceptedReview(investigationId, reviewId);
            await queryClient.invalidateQueries({
              queryKey: queryKeys.investigationReasoningV1(auth.activeCustomerId, investigationId),
            });
          }}
          onLoadEvidenceReviews={() => getInvestigationV1EvidenceReviews(investigationId).then((result) => result.reviews)}
        /> : null}
        {workspaceView === 'advanced' && !conversationFocus.isFocused && !selectedV1 && !selectedLegacy ? <section className="r42-investigation-section" role="status">V1 reasoning mode could not be verified. Diagnostic actions are unavailable until the authenticated mode check succeeds.</section> : null}
        {workspaceView === 'advanced' && !conversationFocus.isFocused && selectedLegacy ? <ActiveReasoningPanel
          reasoning={reasoningState}
          inquiries={reasoningInquiryItems}
          freshness={reasoningFreshness.data?.freshness ?? null}
          history={reasoningHistory.data?.history ?? []}
          canContribute={canContribute}
          canAddEvidence={canContribute && auth.can(permissions.documentsUpload)}
          canReviewRelationships={canUpdate && status !== 'CLOSED' && status !== 'ARCHIVED'}
          investigationId={investigationId}
          busyInquiryId={reasoningActionBusy}
          actionError={reasoningActionError}
          retryBusy={reasoningRetryBusy}
          onRetryReasoning={retryActiveReasoning}
          onRespond={respondToReasoningInquiry}
          onAddEvidence={addEvidenceForInquiry}
          onOpenEvidenceReference={openReasoningEvidence}
        /> : null}

        {workspaceView === 'advanced' && !conversationFocus.isFocused ? <InvestigationAiRoleStrip /> : null}

        <section className="r42-investigation-section r42-investigation-conversation" data-focus-mode={conversationFocus.isFocused ? 'focused' : 'overview'}>
          <div className="r42-investigation-section-head r42-investigation-conversation-head">
            <div>
              <span className="r42-eyebrow">Shared investigation</span>
              <h2>{conversationFocus.isFocused ? data.investigation.title : workspaceView === 'operational' ? 'Talk with your team or Ace' : 'Work the problem together'}</h2>
              {workspaceView === 'operational' && !conversationFocus.isFocused ? <p className="r42-investigation-chat-hint">Team updates and Ace answers share one investigation history. Choose where to send each message.</p> : null}
            </div>
            {conversationId ? <button
              type="button"
              className="r42-investigation-focus-toggle"
              onClick={conversationFocus.toggleFocus}
              aria-pressed={conversationFocus.isFocused}
              aria-label={conversationFocus.isFocused ? 'Back to investigation' : 'Focus Investigation conversation'}
            >
              <i className={`bi ${conversationFocus.isFocused ? 'bi-arrows-angle-contract' : 'bi-arrows-angle-expand'}`} aria-hidden="true" />
              <span>{conversationFocus.isFocused ? 'Back to investigation' : 'Focus conversation'}</span>
            </button> : null}
          </div>
          {conversationFocus.isFocused ? <div className="r42-investigation-focus-context" aria-label="Investigation focus context">
            <span className="r42-eyebrow">Where things stand</span>
            {workspaceView === 'operational' && selectedV1 ? <p>Return to the investigation view to see the latest verified reasoning status. Focus mode keeps the shared conversation in front.</p> : assessment ? <div className="r42-investigation-focus-assessment"><Route42InlineMarkdown content={assessment.text} /></div> : <p>Nothing has been summarized yet. Use team updates, Ace, and evidence to build a shared understanding.</p>}
          </div> : null}
          {conversation.isPending ? <div className="r42-inline-state">Loading shared investigation…</div> : conversation.isError || !conversation.data ? <div className="r42-inline-state r42-inline-state--error">Route42 couldn't load the shared investigation thread.</div> : <>
            <div className="r42-investigation-turns" ref={investigationChatScroll.viewportRef} onScroll={investigationChatScroll.onScroll}>
              {conversation.hasOlder ? <div className="r42-thread-history-control"><button type="button" onClick={() => void historyLoader.loadOlder()} disabled={conversation.isFetchingOlder}><i className="bi bi-clock-history" aria-hidden="true" /> {conversation.isFetchingOlder ? 'Loading earlier updates…' : 'Load earlier updates'}{hiddenTurnCount ? <span>{hiddenTurnCount}</span> : null}</button></div> : null}
              {conversation.data.turns.length ? allInvestigationTurns.map((turn) => <InvestigationThreadEntry
                key={turn.turn_id}
                turn={turn}
                currentUserId={auth.profile?.user.user_id}
                actorName={participantNames.get(turn.user_id)}
                role={data.investigation.participant_roles[turn.user_id]}
                compact={workspaceView === 'operational' && !conversationFocus.isFocused}
                onEvidence={() => { setPinnedEvidence(null); if (turn.answer) setEvidence(turn.answer); }}
              />) : <div className="r42-investigation-section-empty">Post a team update to preserve what happened, or ask Ace when the team wants Route42 to reason over the Investigation and grounded machine evidence.</div>}
              {threadAction === 'ask' ? <ThinkingState /> : null}
            </div>
            <InvestigationComposer variant={workspaceView === 'operational' ? 'operational' : 'advanced'} focusRequest={workspaceView === 'operational' ? composerFocus : null} canContribute={canContribute} canAsk={canAsk} busyAction={threadAction} onPostUpdate={postTeamUpdate} onAskAce={askAce} />
          </>}
        </section>

        {workspaceView === 'advanced' ? <>
        <InvestigationKnowledgeSection
          eyebrow="Shared understanding"
          title="What we know"
          count={findings.length}
          summary={knowledgeSummary(findings.length, 'thing the team has recorded', 'things the team has recorded')}
          emptyText="Nothing has been recorded as known yet."
          actionLabel={canContribute ? 'Add what you know' : undefined}
          onAction={canContribute ? () => setContentType('EVIDENCE_BACKED_FINDING') : undefined}
        >
          <div className="r42-investigation-finding-list">{findings.map((item) => <Finding key={item.content_id ?? item.text} item={item} kind="What we know" />)}</div>
        </InvestigationKnowledgeSection>

        {!reasoningState ? <InvestigationKnowledgeSection
          eyebrow="Working explanations"
          title="Possible causes"
          definition="Things that might explain the problem. They are not confirmed yet."
          count={hypotheses.length}
          summary={knowledgeSummary(hypotheses.length, 'possible cause is being tracked', 'possible causes are being tracked')}
          emptyText="No possible causes are being tracked yet."
          actionLabel={canContribute ? 'Add possible cause' : undefined}
          onAction={canContribute ? () => setContentType('USER_HYPOTHESIS') : undefined}
        >
          <div className="r42-investigation-finding-list">{hypotheses.map((item) => <Finding key={item.content_id ?? item.text} item={item} kind="Possible cause" />)}</div>
        </InvestigationKnowledgeSection> : null}

        {!reasoningState ? <InvestigationKnowledgeSection
          eyebrow="Next checks"
          title="What we still need to check"
          definition="Information that could change the team's understanding."
          count={questions.length}
          summary={questionSummary(questions.length)}
          emptyText="Nothing is waiting to be checked right now."
          actionLabel={canContribute ? 'Add something to check' : undefined}
          onAction={canContribute ? () => setContentType('OPEN_QUESTION') : undefined}
        >
          <ul className="r42-investigation-questions">{questions.map((question, index) => <li key={`${question}-${index}`}><Route42InlineMarkdown content={question} /></li>)}</ul>
        </InvestigationKnowledgeSection> : null}
        </> : null}
      </div>

      {workspaceView === 'advanced' ? <aside className="r42-investigation-context" aria-label="Investigation context">
        <section><span className="r42-eyebrow">What we're trying to figure out</span><InvestigationExpandableText text={data.investigation.objective} maxCharacters={260} className="r42-investigation-context-objective" /></section>
        <section><span className="r42-eyebrow">What supports this</span><strong>{data.counts.evidence} pinned</strong>{data.pinned_evidence.length ? <><div className="r42-investigation-evidence-list">{visiblePinnedEvidence.map((item, index) => <button type="button" key={item.evidence_id} onClick={() => { setEvidence(null); setPinnedEvidence(item); }} aria-label={`Open pinned evidence ${humanArtifact(item.artifact_type)} ${index + 1}`}><i className="bi bi-paperclip" aria-hidden="true" /><span><strong>{humanArtifact(item.artifact_type)}</strong><small>{item.note || item.artifact_id}</small></span><i className="bi bi-chevron-right r42-investigation-evidence-arrow" aria-hidden="true" /></button>)}</div>{hiddenPinnedEvidenceCount ? <button type="button" className="r42-investigation-evidence-more" onClick={() => setVisiblePinnedEvidenceCount((count) => Math.min(data.pinned_evidence.length, count + 10))}>Show {Math.min(10, hiddenPinnedEvidenceCount)} more <span>{hiddenPinnedEvidenceCount} remaining</span></button> : null}</> : <p>No evidence pinned yet. Promoting a grounded answer preserves its supporting evidence here.</p>}</section>
        <section><span className="r42-eyebrow">Team</span><strong>{data.investigation.participant_user_ids.length} contributor{data.investigation.participant_user_ids.length === 1 ? '' : 's'}</strong><p>Everyone with access to this machine can view the Investigation. Your role: {data.access?.current_user_role ?? data.investigation.participant_roles[auth.profile?.user.user_id ?? ''] ?? 'VIEWER'}.</p></section>
        {data.investigation.source_conversation_id ? <section><span className="r42-eyebrow">Started from</span><Link to={`/machines/${machineId}/conversations/${data.investigation.source_conversation_id}`}>Open source conversation <i className="bi bi-arrow-up-right" /></Link></section> : null}
      </aside> : null}
    </div>

    {showClose ? <CloseInvestigationDialog busy={busy} error={error} onClose={() => { if (!busy) { setShowClose(false); setError(null); } }} onReviewKnowledge={(closure) => reviewInvestigationMachineKnowledge(investigationId, closure)} onSubmit={closeInvestigation} /> : null}
    {contentType ? <AddInvestigationContentDialog type={contentType} busy={contentBusy} error={contentError} onClose={() => { if (!contentBusy) { setContentType(null); setContentError(null); } }} onSubmit={saveContent} /> : null}
    {evidence ? <EvidencePanel machineId={machineId} answer={evidence} documents={documents.data?.documents ?? []} onClose={() => setEvidence(null)} /> : null}
    {pinnedEvidence ? <PinnedEvidencePanel machineId={machineId} evidence={pinnedEvidence} turns={conversation.data?.turns ?? []} documents={documents.data?.documents ?? []} sourceConversationId={data.investigation.source_conversation_id} onClose={() => setPinnedEvidence(null)} /> : null}
  </section>;
}

function latest(items?: InvestigationMemoryItemDto[] | null) { return items?.length ? items[items.length - 1] : null; }
function knowledgeSummary(count: number, singular: string, plural: string) { return count === 0 ? 'Nothing recorded yet.' : `${count} ${count === 1 ? singular : plural}.`; }
function questionSummary(count: number) { return count === 0 ? 'Nothing is waiting to be checked.' : `${count} ${count === 1 ? 'check remains open' : 'checks remain open'}.`; }
function Finding({ item, kind }: { item: InvestigationMemoryItemDto; kind: string }) { return <article className="r42-investigation-finding"><span>{kind}</span><Route42Markdown content={item.text} />{item.supporting_evidence_ids?.length ? <small>{item.supporting_evidence_ids.length} supporting evidence item{item.supporting_evidence_ids.length === 1 ? '' : 's'}</small> : null}</article>; }
function humanArtifact(value: string) { return value.toLocaleLowerCase().replaceAll('_', ' ').replace(/\b\w/g, (char) => char.toLocaleUpperCase()); }
function apiMessage(value: unknown) { return value instanceof ApiError ? value.message : 'Route42 could not complete the investigation action. Please try again.'; }
